import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'

import {
  selectAmplitudeApiKey,
  selectGrowthBookExperiments,
  selectStripeAccountId,
  selectStripeAccountName,
  selectUtmTags,
} from 'root-redux/selects/common'
import { selectUUID, selectUserStatus } from 'root-redux/selects/user'

import { useBeaconFlushOnPageHide } from 'hooks/useBeaconFlushOnPageHide'
import { useCookieConsentAnswer } from 'hooks/useCookieConsentAnswer'
import { useDynamicTextsFromUrl } from 'hooks/useDynamicTextsFromUrl'
import { growthBookInstance } from 'hooks/useGrowthBookInitialization'

import { getEnvironment } from 'helpers/getEnvironment'
import { getActiveGbSegmentNames } from 'helpers/getGbSegmentNames'
import { getQueryParamsFromSearchString } from 'helpers/getQueryParamsFromSearchString'

import { Amplitude } from 'services/amplitude.service'
import { eventLogger } from 'services/eventLogger.service'

import { EventLoggerInstanceName } from 'root-constants'

export function useAmplitudeInitialization(cohort = ''): Amplitude | null {
  const { search } = useLocation()
  const uuid = useSelector(selectUUID)
  const amplitudeApiKey = useSelector(selectAmplitudeApiKey)
  const userStatus = useSelector(selectUserStatus)
  const stripeAccountId = useSelector(selectStripeAccountId)
  const stripeAccountName = useSelector(selectStripeAccountName)
  const utmTags = useSelector(selectUtmTags)
  const experimentsMap = useSelector(selectGrowthBookExperiments)
  const { canUseAnalytics, hasAnsweredConsent } = useCookieConsentAnswer()
  const { eventProperties: dynamicTextsEventProperties } =
    useDynamicTextsFromUrl()

  const [amplitudeInstance, setAmplitudeInstance] = useState<Amplitude | null>(
    null,
  )

  const { isProdEnvironment, isTestEnvironment } = getEnvironment()

  useBeaconFlushOnPageHide(amplitudeInstance)

  useEffect(() => {
    if (eventLogger.isAmplitudeActive || !canUseAnalytics) {
      return undefined
    }

    if (uuid && amplitudeApiKey) {
      const amplitudeService = new Amplitude({
        apiKey: amplitudeApiKey,
        instanceName: EventLoggerInstanceName.AMPLITUDE,
        userId: uuid,
        utmTags: Object.entries(utmTags).length
          ? utmTags
          : getQueryParamsFromSearchString(search),
        isTestEnvironment,
        isDevEnvironment: !isProdEnvironment,
        trackSegments: true,
        dynamicTexts: dynamicTextsEventProperties,
      })
      amplitudeService.configure({ cohort })
      amplitudeService.updateGbSegments(getActiveGbSegmentNames(experimentsMap))

      if (stripeAccountId && stripeAccountName) {
        amplitudeService?.updateUserProperties(
          stripeAccountId,
          stripeAccountName,
        )
      }

      eventLogger.init(amplitudeService)
      setAmplitudeInstance(amplitudeService)
    }

    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    uuid,
    amplitudeApiKey,
    stripeAccountId,
    stripeAccountName,
    userStatus,
    canUseAnalytics,
  ])

  useEffect(() => {
    if (!hasAnsweredConsent || canUseAnalytics) return

    eventLogger.stopBufferingAmplitudeEvents()
  }, [hasAnsweredConsent, canUseAnalytics])

  useEffect(() => {
    if (!amplitudeInstance) return undefined

    const syncActiveGbSegments = (): void => {
      amplitudeInstance.updateGbSegments(
        getActiveGbSegmentNames(experimentsMap),
      )
    }

    syncActiveGbSegments()

    const unsubscribe = growthBookInstance.subscribe(syncActiveGbSegments)

    return () => unsubscribe()
  }, [amplitudeInstance, experimentsMap])

  useEffect(() => {
    amplitudeInstance?.updateDynamicTexts(dynamicTextsEventProperties)
  }, [amplitudeInstance, dynamicTextsEventProperties])

  return amplitudeInstance
}
